import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  Copy, Users, Crown, Zap, Sparkles, Loader2, Camera, ArrowRight,
  Heart, AlertTriangle, Wand2,
} from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import confetti from "canvas-confetti";
import Header, { LogoBadge } from "@/components/Header";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Sticker } from "@/components/Stickers";

// Brand-coloured confetti palette
const CONFETTI_COLORS = ["#ff2d8a", "#ff6a3d", "#ffd84d", "#8a5cf6", "#ffffff"];

function burstConfetti(element) {
  // Locate the burst origin from the freshly-mounted card so the explosion
  // appears to come out of the card itself.
  let originX = 0.5, originY = 0.5;
  if (element && element.getBoundingClientRect) {
    const rect = element.getBoundingClientRect();
    originX = (rect.left + rect.width / 2) / window.innerWidth;
    originY = (rect.top + rect.height / 2) / window.innerHeight;
  }
  confetti({
    particleCount: 80,
    spread: 75,
    startVelocity: 35,
    gravity: 0.9,
    ticks: 160,
    scalar: 0.95,
    colors: CONFETTI_COLORS,
    origin: { x: originX, y: Math.max(0.1, Math.min(0.9, originY)) },
    shapes: ["circle", "square"],
    disableForReducedMotion: true,
  });
}

/**
 * One participant's own reveal — their selfie + the candidate the system
 * picked for them in Doppel + Chaos mode. We never compare them face-to-face
 * with another room member by design (campus / same-sex friend safety).
 */
function RevealCard({ reveal, badge, justJoined }) {
  const u = reveal.user;
  const dop = reveal.doppel;
  const ch = reveal.chaos;
  const cardRef = useRef(null);

  // First-mount confetti burst when this card represents a freshly-joined
  // participant (detected one level up). `disableForReducedMotion` inside
  // burstConfetti respects the user's OS preference.
  useEffect(() => {
    if (justJoined && cardRef.current) {
      // small delay so the slide-in animation is already underway
      const t = setTimeout(() => burstConfetti(cardRef.current), 280);
      return () => clearTimeout(t);
    }
  }, [justJoined]);

  return (
    <motion.div
      ref={cardRef}
      layout
      initial={justJoined ? { opacity: 0, y: 28, scale: 0.94 } : false}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ type: "spring", stiffness: 220, damping: 22 }}
      className="crush-frame p-5 relative bg-white"
      data-testid={`reveal-card-${u.id}`}
    >
      <AnimatePresence>
        {justJoined ? (
          <motion.div
            key="just-joined-tag"
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.6 }}
            transition={{ delay: 0.15, type: "spring", stiffness: 300, damping: 18 }}
            className="absolute -top-3 left-4 px-3 py-1 rounded-full bg-pink-600 text-white text-[10px] font-display font-bold uppercase tracking-widest shadow-md"
            data-testid="just-joined-pill"
          >
            ✦ Just dropped
          </motion.div>
        ) : null}
      </AnimatePresence>
      {badge ? (
        <div className="absolute -top-3 -right-3 px-3 py-1.5 rounded-full bg-slate-900 text-white text-[11px] font-display font-bold uppercase tracking-widest shadow-lg shadow-pink-200 inline-flex items-center gap-1.5">
          <badge.icon className="w-3 h-3" /> {badge.label}
        </div>
      ) : null}
      <div className="flex items-center gap-3">
        <img
          src={u.photo_url}
          alt={u.name}
          className="w-16 h-16 rounded-2xl object-cover border-2 border-white shadow"
        />
        <div className="flex-1 min-w-0">
          <div className="font-display text-xl font-bold text-slate-900 truncate">{u.name}</div>
          <div className="text-xs font-body text-slate-500">dropped their reveal</div>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3">
        {/* Doppel pick */}
        <div className="rounded-2xl border-2 border-pink-100 bg-gradient-to-br from-pink-50 to-white p-3">
          <div className="flex items-center gap-1.5 text-[10px] font-display font-bold uppercase tracking-widest text-pink-700">
            <Heart className="w-3 h-3" /> Doppel
          </div>
          {dop?.profile ? (
            <>
              <div className="mt-2 flex items-center gap-2">
                <img src={dop.profile.photo_url} alt={dop.profile.name} className="w-10 h-10 rounded-lg object-cover" />
                <div className="min-w-0">
                  <div className="font-display font-bold text-slate-900 text-sm truncate">{dop.profile.name}</div>
                  <div className="text-[10px] font-body text-slate-500 truncate">{(dop.explanation || [])[0]}</div>
                </div>
              </div>
              <div className="mt-2 font-display text-2xl font-bold crush-text-grad">{dop.score}%</div>
            </>
          ) : (
            <div className="mt-3 text-xs font-body text-slate-400">No doppel yet</div>
          )}
        </div>

        {/* Chaos pick */}
        <div className="rounded-2xl border-2 border-purple-100 bg-gradient-to-br from-purple-50 to-white p-3">
          <div className="flex items-center gap-1.5 text-[10px] font-display font-bold uppercase tracking-widest text-purple-700">
            <Zap className="w-3 h-3" /> Chaos
          </div>
          {ch?.profile ? (
            <>
              <div className="mt-2 flex items-center gap-2">
                <img src={ch.profile.photo_url} alt={ch.profile.name} className="w-10 h-10 rounded-lg object-cover" />
                <div className="min-w-0">
                  <div className="font-display font-bold text-slate-900 text-sm truncate">{ch.profile.name}</div>
                  <div className="text-[10px] font-body text-slate-500 truncate">{(ch.explanation || [])[0]}</div>
                </div>
              </div>
              <div className="mt-2 font-display text-2xl font-bold text-purple-700">{ch.score}%</div>
            </>
          ) : (
            <div className="mt-3 text-xs font-body text-slate-400">No chaos yet</div>
          )}
        </div>
      </div>
    </motion.div>
  );
}

/**
 * Public-preview teaser shown to guests (not logged in or not onboarded).
 */
function ComparePreview({ roomId, preview }) {
  const host = preview?.host;
  const hostName = host?.name || "A friend";
  const others = (preview?.participants || []).filter((p) => p?.name && p?.name !== hostName);
  const joinHref = `/auth?return_to=${encodeURIComponent(`/doppel-room/${roomId}`)}&source=doppel_room`;
  return (
    <div className="crush-bg min-h-screen" data-testid="compare-preview-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16">
        <Link to="/"><LogoBadge /></Link>

        <div className="crush-frame mt-8 p-6 sm:p-10 relative overflow-hidden">
          <Sticker kind="bolt" size={72} className="absolute -top-6 -right-4 rotate-12" color="#8a5cf6" />
          <Sticker kind="spark" size={56} className="absolute bottom-8 left-4 -rotate-6 hidden md:inline-flex" />

          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple-100 text-purple-700 font-display font-bold text-xs uppercase tracking-widest">
            <Users className="w-3 h-3" /> Doppel Room invite
          </div>
          <h1 className="font-display text-5xl sm:text-6xl font-bold leading-[0.95] mt-3">
            <span className="crush-sticker-text-dark">{hostName} </span>
            <span className="crush-sticker-text crush-text-grad">dropped a reveal.</span>
          </h1>
          <p className="mt-4 text-slate-600 font-body text-lg max-w-xl">
            Drop your selfie. Get your own Doppel + Chaos reveal. We&apos;ll crown the strongest twin, the wildest chaos pick and the funniest reveal in the room.
          </p>

          {/* Participants stack */}
          <div className="mt-7 flex items-center gap-4 flex-wrap">
            <div className="flex -space-x-3">
              {host?.photo_url ? (
                <img
                  src={host.photo_url}
                  alt={host.name}
                  title={host.name}
                  className="w-16 h-16 rounded-full object-cover border-4 border-white shadow-lg"
                  data-testid="preview-host-photo"
                />
              ) : null}
              {others.slice(0, 5).map((p) => (
                <img
                  key={p.id || p.user_id || p.photo_url}
                  src={p.photo_url}
                  alt={p.name}
                  title={p.name}
                  className="w-16 h-16 rounded-full object-cover border-4 border-white shadow-lg"
                />
              ))}
              {preview?.participant_count > 6 ? (
                <div className="w-16 h-16 rounded-full bg-slate-900 text-white grid place-items-center font-display font-bold border-4 border-white shadow-lg">
                  +{preview.participant_count - 6}
                </div>
              ) : null}
            </div>
            <div className="font-body text-slate-600">
              <div className="font-display font-bold text-slate-900 text-base">
                {preview?.participant_count} {preview?.participant_count === 1 ? "person" : "people"} in the room
              </div>
              <div className="text-sm">{preview?.title || "Who got the strongest Doppel?"}</div>
            </div>
          </div>

          {/* CTAs */}
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link
              to={joinHref}
              className="crush-cta rounded-full font-display font-bold px-7 py-4 text-lg inline-flex items-center justify-center gap-2"
              data-testid="preview-join-btn"
            >
              <Camera className="w-5 h-5" /> Drop my reveal <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to={`/auth?mode=login&return_to=${encodeURIComponent(`/doppel-room/${roomId}`)}`}
              className="crush-secondary rounded-full font-display font-bold px-7 py-4 text-lg inline-flex items-center justify-center gap-2"
              data-testid="preview-login-btn"
            >
              I already have an account
            </Link>
          </div>

          <p className="mt-5 text-xs text-slate-500 font-body max-w-xl">
            Your selfie stays private — it&apos;s only used to compute your own Doppel and Chaos pick. We never compare friends to each other inside the room.
          </p>
        </div>
      </div>
    </div>
  );
}

function ComparePreviewMissing({ roomId }) {
  return (
    <div className="crush-bg min-h-screen" data-testid="compare-preview-missing">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16">
        <Link to="/"><LogoBadge /></Link>
        <div className="crush-frame mt-8 p-10 text-center">
          <h1 className="font-display text-4xl font-bold">Room not found</h1>
          <p className="mt-3 text-slate-600 font-body">
            This Doppel Room <span className="font-mono text-slate-900">{roomId}</span> has expired or never existed.
          </p>
          <Link
            to="/"
            className="crush-cta rounded-full font-display font-bold px-6 py-3 inline-flex items-center gap-2 mt-6"
          >
            Go home <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}

const BADGE_DEFS = [
  { key: "strongest_twin",  label: "Strongest Twin Energy", icon: Crown,    color: "from-pink-50 to-white",    border: "border-pink-200",    text: "text-pink-700" },
  { key: "wildest_chaos",   label: "Wildest Chaos Pick",    icon: Zap,      color: "from-purple-50 to-white",  border: "border-purple-200",  text: "text-purple-700" },
  { key: "funniest_reveal", label: "Funniest Reveal",       icon: Sparkles, color: "from-amber-50 to-white",   border: "border-amber-200",   text: "text-amber-700" },
  { key: "plot_twist",      label: "Plot Twist Winner",     icon: Wand2,    color: "from-orange-50 to-white",  border: "border-orange-200",  text: "text-orange-700" },
];

function BadgeStrip({ badges, changedKeys }) {
  const items = BADGE_DEFS.map((b) => ({ def: b, value: badges?.[b.key] })).filter((x) => x.value);
  const plotTwistRef = useRef(null);

  // Plot Twist is the rarest outcome — fire a small secondary burst from the
  // badge card itself when a NEW plot-twist holder is crowned.
  useEffect(() => {
    if (changedKeys?.has("plot_twist") && plotTwistRef.current) {
      const rect = plotTwistRef.current.getBoundingClientRect();
      const originX = (rect.left + rect.width / 2) / window.innerWidth;
      const originY = (rect.top + rect.height / 2) / window.innerHeight;
      confetti({
        particleCount: 40,
        spread: 55,
        startVelocity: 28,
        gravity: 1.0,
        ticks: 120,
        scalar: 0.85,
        colors: ["#8a5cf6", "#ff6a3d", "#ffd84d"],
        origin: { x: originX, y: Math.max(0.1, Math.min(0.9, originY)) },
        shapes: ["star", "circle"],
        disableForReducedMotion: true,
      });
    }
  }, [changedKeys]);

  if (items.length === 0) return null;
  return (
    <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="badge-strip">
      {items.map(({ def, value }) => {
        const changed = changedKeys?.has(def.key);
        return (
          <motion.div
            key={def.key}
            ref={def.key === "plot_twist" ? plotTwistRef : null}
            layout
            animate={changed ? {
              scale: [1, 1.06, 1],
              boxShadow: [
                "0 0 0 0 rgba(255,45,138,0)",
                "0 0 0 6px rgba(255,45,138,0.18)",
                "0 0 0 0 rgba(255,45,138,0)",
              ],
            } : {}}
            transition={{ duration: 0.9, ease: "easeOut" }}
            className={`crush-frame p-4 bg-gradient-to-br ${def.color} ${def.border}`}
            data-testid={`badge-${def.key}`}
          >
            <div className={`text-[10px] font-display font-bold uppercase tracking-widest ${def.text} inline-flex items-center gap-1.5`}>
              <def.icon className="w-3 h-3" /> {def.label}
            </div>
            <div className="mt-2 flex items-center gap-2.5">
              <img src={value.user.photo_url} alt={value.user.name} className="w-12 h-12 rounded-xl object-cover" />
              <div className="min-w-0">
                <div className="font-display font-bold text-slate-900 truncate">{value.user.name}</div>
                <div className="text-xs font-body text-slate-500">
                  {def.key === "plot_twist"
                    ? `Chaos ${value.chaos_score}% > Doppel ${value.doppel_score}%`
                    : `${value.score}%`}
                </div>
              </div>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}

export default function Compare() {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const { user, bootstrapped } = useAuth();
  const isGuest = bootstrapped && (!user || !user.onboarding_complete);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [room, setRoom] = useState(null);
  const [preview, setPreview] = useState(null);
  const [copied, setCopied] = useState(false);
  // Track which reveals/badges just changed so we can animate them once.
  const [justJoinedIds, setJustJoinedIds] = useState(() => new Set());
  const [changedBadgeKeys, setChangedBadgeKeys] = useState(() => new Set());
  const prevRevealIdsRef = useRef(new Set());
  const prevBadgeHoldersRef = useRef({});
  const firstLoadRef = useRef(true);

  // Diff helper — runs INSIDE the fetch callback (not in an effect) so we
  // can update animation state + room state in lockstep without the
  // set-state-in-effect lint pattern. Returns the new badge-holder snapshot.
  const applyRoomUpdate = (data) => {
    const ids = new Set((data.reveals || []).map((r) => r.user.id));
    const newHolders = Object.fromEntries(
      Object.keys(data.badges || {}).map((k) => [k, data.badges[k]?.user?.id || null])
    );
    if (firstLoadRef.current) {
      firstLoadRef.current = false;
      prevRevealIdsRef.current = ids;
      prevBadgeHoldersRef.current = newHolders;
    } else {
      const fresh = new Set();
      for (const id of ids) if (!prevRevealIdsRef.current.has(id)) fresh.add(id);
      const changed = new Set();
      for (const [k, v] of Object.entries(newHolders)) {
        if (v && v !== prevBadgeHoldersRef.current[k]) changed.add(k);
      }
      prevRevealIdsRef.current = ids;
      prevBadgeHoldersRef.current = newHolders;
      if (fresh.size > 0) {
        setJustJoinedIds(fresh);
        setTimeout(() => setJustJoinedIds(new Set()), 2200);
      }
      if (changed.size > 0) {
        setChangedBadgeKeys(changed);
        setTimeout(() => setChangedBadgeKeys(new Set()), 1400);
      }
    }
    setRoom(data);
  };

  const shareUrl = useMemo(() => {
    if (!room?.id) return "";
    return `${window.location.origin}/doppel-room/${room.id}`;
  }, [room]);

  // Remember which room a logged-out visitor wanted
  useEffect(() => {
    if (roomId && bootstrapped && !user) {
      try { localStorage.setItem("dc_pending_compare_room", roomId); } catch { /* storage blocked */ }
    }
  }, [roomId, user, bootstrapped]);

  useEffect(() => {
    if (!bootstrapped) return;
    let cancelled = false;
    const run = async () => {
      try {
        if (roomId && isGuest) {
          const { data } = await api.get(`/compare/${roomId}/preview`);
          if (!cancelled) setPreview(data);
        } else if (roomId) {
          const { data } = await api.get(`/compare/${roomId}`);
          if (!cancelled) applyRoomUpdate(data);
        } else {
          const { data } = await api.post("/compare", { title: "Who got the strongest Doppel?" });
          if (!cancelled) navigate(`/doppel-room/${data.id}`, { replace: true });
        }
      } catch (err) {
        if (!cancelled) setError(formatApiError(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    const interval = roomId && !isGuest ? setInterval(async () => {
      try {
        const { data } = await api.get(`/compare/${roomId}`);
        if (!cancelled) applyRoomUpdate(data);
      } catch { /* polling errors are non-fatal */ }
    }, 6000) : null;
    return () => { cancelled = true; if (interval) clearInterval(interval); };
  }, [roomId, navigate, isGuest, bootstrapped]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard blocked */ }
  };

  // -------- Guest / not-onboarded view --------
  if (bootstrapped && isGuest) {
    if (loading) {
      return (
        <div className="crush-bg min-h-screen grid place-items-center font-display text-pink-600">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      );
    }
    if (!preview || preview.valid === false) {
      return <ComparePreviewMissing roomId={roomId} />;
    }
    return <ComparePreview roomId={roomId} preview={preview} />;
  }

  // -------- Authenticated + onboarded view --------
  // Build a per-user "best badge" map so each card highlights at most one badge.
  const bestBadgePerUser = (() => {
    const map = {};
    if (!room?.badges) return map;
    for (const def of BADGE_DEFS) {
      const b = room.badges[def.key];
      if (b?.user?.id && !map[b.user.id]) map[b.user.id] = { ...def };
    }
    return map;
  })();

  return (
    <div className="crush-bg min-h-screen" data-testid="compare-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-16">
        <Header />

        <div className="crush-frame mt-4 p-6 sm:p-10 relative overflow-hidden">
          <Sticker kind="bolt" size={64} className="absolute -top-6 -right-4 rotate-12" color="#8a5cf6" />
          <Sticker kind="spark" size={56} className="absolute bottom-8 left-4 -rotate-6 hidden md:inline-flex" />

          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple-100 text-purple-700 font-display font-bold text-xs uppercase tracking-widest">
            <Users className="w-3 h-3" /> Doppel Room
          </div>
          <h1 className="font-display text-5xl sm:text-6xl font-bold leading-[0.95] mt-3">
            <span className="crush-sticker-text-dark">{room?.title?.split(" ").slice(0, 2).join(" ") || "Who got the"} </span>
            <span className="crush-sticker-text crush-text-grad">{room?.title?.split(" ").slice(2).join(" ") || "strongest Doppel?"}</span>
          </h1>
          <p className="mt-4 text-slate-600 font-body text-lg max-w-2xl">
            Drop the link in your group chat. Each friend who joins gets their own Doppel + Chaos reveal — and we crown the wildest results in the room.
          </p>

          {loading ? (
            <div className="mt-10 text-center text-pink-600 font-display inline-flex items-center gap-2">
              <Loader2 className="w-5 h-5 animate-spin" /> Spinning up the room…
            </div>
          ) : error ? (
            <div className="mt-6 text-rose-600 font-body inline-flex items-center gap-2" data-testid="compare-error">
              <AlertTriangle className="w-4 h-4" /> {error}
            </div>
          ) : room ? (
            <>
              {/* Share link */}
              <div className="mt-6 max-w-2xl flex items-center gap-2 bg-white rounded-full p-1.5 border-2 border-slate-100">
                <input
                  value={shareUrl}
                  readOnly
                  className="flex-1 bg-transparent px-3 py-2 font-body text-sm truncate outline-none"
                  data-testid="compare-share-url"
                />
                <button
                  onClick={copy}
                  className="crush-cta rounded-full px-5 py-2 font-display font-bold text-sm inline-flex items-center gap-1.5"
                  data-testid="compare-copy-btn"
                >
                  <Copy className="w-4 h-4" /> {copied ? "Copied" : "Copy"}
                </button>
              </div>

              {/* Participants stack */}
              <div className="mt-6">
                <div className="text-xs font-display font-bold uppercase tracking-widest text-slate-500">
                  In the room ({room.participant_count})
                </div>
                <div className="mt-2 flex -space-x-3" data-testid="compare-participants">
                  {room.participants.map((p) => (
                    <img
                      key={p.id}
                      src={p.photo_url}
                      alt={p.name}
                      title={p.name}
                      className="w-14 h-14 rounded-full object-cover border-2 border-white shadow"
                    />
                  ))}
                  {room.participant_count === 1 ? (
                    <div className="ml-3 font-body text-sm text-slate-500 self-center">
                      Send the link — friends drop their own reveals here.
                    </div>
                  ) : null}
                </div>
              </div>

              {/* Badge strip */}
              <BadgeStrip badges={room.badges} changedKeys={changedBadgeKeys} />

              {/* Per-person reveals */}
              {room.reveals && room.reveals.length > 0 ? (
                <div className="mt-8">
                  <div className="text-xs font-display font-bold uppercase tracking-widest text-slate-500 mb-3">
                    Everyone&apos;s reveal
                  </div>
                  <motion.div layout className="grid sm:grid-cols-2 gap-4" data-testid="reveals-grid">
                    <AnimatePresence initial={false}>
                      {room.reveals.map((r) => (
                        <RevealCard
                          key={r.user.id}
                          reveal={r}
                          badge={bestBadgePerUser[r.user.id]}
                          justJoined={justJoinedIds.has(r.user.id)}
                        />
                      ))}
                    </AnimatePresence>
                  </motion.div>
                </div>
              ) : (
                <div className="mt-10 text-center text-slate-500 font-body">
                  Share the link above — once a friend drops their selfie their reveal lands here.
                </div>
              )}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
